(() => {
  if (window.MagicGift) return;
  const element = document.getElementById('magic-gift-config');
  if (!element) return;
  const config = JSON.parse(element.textContent);
  if (!Number.isSafeInteger(Number(config.variantId)) || Number(config.variantId) <= 0) return;
  const nativeFetch = window.fetch.bind(window);
  const variantId = Number(config.variantId);
  const root = String(config.rootUrl || '/').replace(/\/+$/, '') + '/';
  const cartUrl = `${root}cart`;
  let queue = Promise.resolve();
  let pending = 0;
  let checkoutReplay = false;
  let checkoutWaiting = false;
  let eventScheduled = false;
  let rendering = false;
  let giftFailed = false;
  let mutationVersion = 0;
  const expressState = new WeakMap();

  const isGift = item => Number(item.variant_id || item.id) === variantId;
  const giftText = grams => `${grams} g de CBD`;
  const eligibleSubtotal = cart => Math.max(0, Number(cart.total_price) - cart.items.filter(isGift).reduce((sum, item) => sum + Number(item.final_line_price || 0), 0));
  const earnedGrams = cart => Math.floor(eligibleSubtotal(cart) / config.thresholdCents) * config.gramsPerTier;
  const errorText = config.errorText || 'Le cadeau n’a pas pu être actualisé. Réessayez avant de passer commande.';

  function showError() {
    let error = document.getElementById('magic-gift-error');
    if (!error) {
      error = document.createElement('p');
      error.id = 'magic-gift-error';
      error.setAttribute('role', 'alert');
      error.style.cssText = 'padding:12px;margin:12px 0;border:1px solid currentColor;border-radius:8px;';
    }
    const host = document.querySelector('cart-drawer.active #CartDrawer .drawer__footer') || document.querySelector('#main-cart-footer') || document.querySelector('main') || document.body;
    if (error.parentElement !== host) host.prepend(error);
    error.textContent = errorText;
    error.hidden = false;
  }

  function clearError() {
    giftFailed = false;
    const error = document.getElementById('magic-gift-error');
    if (error) error.hidden = true;
  }

  function lockExpress(locked) {
    document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach(buttons => {
      if (locked) {
        if (!expressState.has(buttons)) expressState.set(buttons, { inert: buttons.inert, busy: buttons.getAttribute('aria-busy') });
        buttons.inert = true;
        buttons.setAttribute('aria-busy', 'true');
      } else if (expressState.has(buttons)) {
        const previous = expressState.get(buttons);
        buttons.inert = previous.inert;
        if (previous.busy === null) buttons.removeAttribute('aria-busy');
        else buttons.setAttribute('aria-busy', previous.busy);
        expressState.delete(buttons);
      }
    });
  }

  function enqueue(task) {
    pending += 1;
    lockExpress(true);
    const result = queue.then(task);
    queue = result.catch(() => {}).finally(() => {
      pending -= 1;
      lockExpress(pending > 0 || giftFailed);
    });
    return result;
  }

  async function jsonRequest(path, body, accept = 'application/json') {
    const response = await nativeFetch(path, body === undefined ? {
      credentials: 'same-origin', cache: 'no-store', headers: { Accept: accept }
    } : {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(`Gift cart request failed (${response.status} ${new URL(path, window.location.origin).pathname})`);
    return response.json();
  }

  async function reconcile() {
    let cart = await jsonRequest(`${cartUrl}.js`);
    let changed = false;
    for (let pass = 0; pass < 5; pass += 1) {
      if (cart.currency && cart.currency !== config.currency) throw new Error('Unsupported gift currency');
      const grams = earnedGrams(cart);
      const gifts = cart.items.filter(isGift);
      const desired = grams > 0 ? gifts.find(item => Number(item.variant_id || item.id) === variantId) : null;
      const unwanted = gifts.filter(item => item !== desired);
      if (unwanted.length) {
        const updates = Object.fromEntries(unwanted.map(item => [item.key, 0]));
        cart = await jsonRequest(`${cartUrl}/update.js`, { updates });
        changed = true;
        continue;
      }
      if (grams > 0 && !desired) {
        await jsonRequest(`${cartUrl}/add.js`, {
          items: [{ id: variantId, quantity: 1, properties: { _magic_gift: 'true', 'Quantité offerte': giftText(grams) } }]
        });
        cart = await jsonRequest(`${cartUrl}.js`);
        changed = true;
        continue;
      }
      if (desired && (Number(desired.price) !== 0 || Number(desired.final_line_price) !== 0)) {
        await jsonRequest(`${cartUrl}/update.js`, { updates: { [desired.key]: 0 }, attributes: { [config.attributeName]: '' } });
        throw new Error('Gift variant must be free');
      }
      if (desired && (desired.quantity !== 1 || desired.properties?._magic_gift !== 'true' || desired.properties?.['Quantité offerte'] !== giftText(grams))) {
        cart = await jsonRequest(`${cartUrl}/change.js`, {
          id: desired.key, quantity: 1,
          properties: { ...(desired.properties || {}), _magic_gift: 'true', 'Quantité offerte': giftText(grams) }
        });
        changed = true;
        continue;
      }
      const attribute = grams > 0 ? giftText(grams) : '';
      if ((cart.attributes?.[config.attributeName] || '') !== attribute) {
        cart = await jsonRequest(`${cartUrl}/update.js`, { attributes: { [config.attributeName]: attribute } });
        changed = true;
        continue;
      }
      clearError();
      return { cart, changed };
    }
    throw new Error('Gift cart did not settle');
  }

  function pageSections() {
    const sections = [];
    if (document.querySelector('cart-drawer')) sections.push({ id: 'cart-drawer', target: '#CartDrawer', source: '#CartDrawer' });
    if (document.getElementById('cart-icon-bubble')) sections.push({ id: 'cart-icon-bubble', target: '#cart-icon-bubble', source: '.shopify-section' });
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (items?.dataset.id) sections.push({ id: items.dataset.id, target: '#main-cart-items .js-contents', source: '.js-contents' });
    if (footer?.dataset.id) sections.push({ id: footer.dataset.id, target: '#main-cart-footer .cart-footer-js-contents', source: '.cart-footer-js-contents' });
    if (document.getElementById('cart-live-region-text')) sections.push({ id: 'cart-live-region-text', target: '#cart-live-region-text', source: '.shopify-section' });
    return sections;
  }

  async function fetchSections(ids) {
    const unique = [...new Set(ids)].filter(Boolean);
    if (!unique.length) return {};
    const result = {};
    for (let offset = 0; offset < unique.length; offset += 5) {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', unique.slice(offset, offset + 5).join(','));
      // /cart negotiates the cart JSON when Accept is application/json, bypassing sections.
      Object.assign(result, await jsonRequest(url.toString(), undefined, 'text/html'));
    }
    const unavailable = unique.filter(id => typeof result[id] !== 'string');
    if (unavailable.length) throw new Error(`Gift cart sections unavailable: ${unavailable.join(', ')}`);
    return result;
  }

  function renderSections(cart, sections, descriptors) {
    rendering = true;
    try {
      const drawer = document.querySelector('cart-drawer');
      const drawerOpen = drawer?.classList.contains('active');
      const activeId = document.activeElement?.id;
      for (const section of descriptors) {
        const target = document.querySelector(section.target);
        if (!target || !sections[section.id]) continue;
        const source = new DOMParser().parseFromString(sections[section.id], 'text/html').querySelector(section.source);
        if (source) target.innerHTML = source.innerHTML;
      }
      for (const target of [drawer, document.querySelector('cart-items'), document.getElementById('main-cart-footer')]) {
        target?.classList.toggle('is-empty', cart.item_count === 0);
      }
      if (drawer && descriptors.some(section => section.id === 'cart-drawer')) {
        const overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
        if (drawerOpen && typeof trapFocus === 'function') {
          const target = cart.item_count ? drawer.querySelector('#CartDrawer') : drawer.querySelector('.drawer__inner-empty');
          const focus = activeId && document.getElementById(activeId) || drawer.querySelector('.drawer__close');
          if (target && focus) trapFocus(target, focus);
        }
      }
      if (typeof initUpsellSlider === 'function') initUpsellSlider();
      document.dispatchEvent(new CustomEvent('magic-gift:updated', { detail: cart }));
    } finally {
      rendering = false;
    }
  }

  function sync(options = {}) {
    return enqueue(async () => {
      try {
        const result = await reconcile();
        if ((result.changed || options.forceRender) && options.render !== false) {
          const descriptors = pageSections();
          const sections = await fetchSections(descriptors.map(section => section.id));
          renderSections(result.cart, sections, descriptors);
        }
        return result.cart;
      } catch (error) {
        console.warn('MagicGift sync:', error.message);
        giftFailed = true;
        showError();
        throw error;
      }
    });
  }

  function request(input, options = {}) {
    mutationVersion += 1;
    return enqueue(async () => {
      const response = await nativeFetch(input, options);
      if (!response.ok) return response;
      let state;
      try {
        state = await response.clone().json();
      } catch (_) {
        return response;
      }
      if (state.status) return response;
      try {
        const result = await reconcile();
        const ids = Object.keys(state.sections || {});
        const otherSections = pageSections().filter(section => !ids.includes(section.id));
        const refreshIds = [...(result.changed ? ids : []), ...otherSections.map(section => section.id)];
        const refreshedSections = await fetchSections(refreshIds);
        const sections = state.sections ? { ...state.sections } : undefined;
        if (sections) ids.forEach(id => { if (refreshedSections[id]) sections[id] = refreshedSections[id]; });
        if (otherSections.length) renderSections(result.cart, refreshedSections, otherSections);
        const finalState = Array.isArray(state.items) && typeof state.total_price === 'number' ? { ...state, ...result.cart } : { ...state };
        if (sections) finalState.sections = sections;
        const headers = new Headers(response.headers);
        headers.delete('content-length');
        headers.set('content-type', 'application/json');
        return new Response(JSON.stringify(finalState), { status: response.status, statusText: response.statusText, headers });
      } catch (error) {
        console.warn('MagicGift request:', error.message);
        giftFailed = true;
        showError();
        setTimeout(showError, 0);
        const headers = new Headers(response.headers);
        headers.delete('content-length');
        headers.set('content-type', 'application/json');
        return new Response(JSON.stringify({ ...state, magicGiftPending: true }), { status: response.status, statusText: response.statusText, headers });
      }
    });
  }

  async function waitForIdle() {
    let observed;
    do {
      observed = queue;
      await observed;
    } while (observed !== queue || pending > 0);
    if (giftFailed) throw new Error('Gift checkout is not ready');
  }

  async function guardCheckout(event) {
    if (checkoutReplay || event.defaultPrevented) return;
    const target = event.type === 'submit' ? event.submitter : event.target.closest('a,button,input');
    const anchor = target?.closest('a[href]');
    const href = anchor && new URL(anchor.href, window.location.origin);
    const isCheckoutLink = href && href.origin === window.location.origin && /\/checkout(?:\/|$)/.test(href.pathname);
    const isCheckoutButton = target?.name === 'checkout' || target?.classList.contains('cart__checkout-button');
    if (!isCheckoutLink && !isCheckoutButton) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutWaiting) return;
    checkoutWaiting = true;
    const form = target?.form || (event.type === 'submit' ? event.target : null);
    const targetId = target?.id;
    const formId = form?.id;
    try {
      let cart;
      let checkoutVersion;
      do {
        checkoutVersion = mutationVersion;
        cart = await sync({ forceRender: true });
        await waitForIdle();
      } while (checkoutVersion !== mutationVersion);
      checkoutReplay = true;
      if (isCheckoutLink) {
        window.location.assign(href.href);
      } else {
        const liveForm = formId && document.getElementById(formId) || form;
        const liveButton = targetId && document.getElementById(targetId) || liveForm?.querySelector('[name="checkout"]') || target;
        if (!liveForm?.isConnected || liveButton?.form !== liveForm) throw new Error('Checkout form unavailable');
        const originalUpdates = Array.from(liveForm.elements).filter(input => input.name === 'updates[]').map(input => ({ input, disabled: input.disabled }));
        const finalUpdates = cart.items.map(item => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'updates[]';
          input.value = String(item.quantity);
          liveForm.appendChild(input);
          return input;
        });
        originalUpdates.forEach(({ input }) => { input.disabled = true; });
        try {
          liveForm.requestSubmit(liveButton);
        } finally {
          finalUpdates.forEach(input => input.remove());
          originalUpdates.forEach(({ input, disabled }) => { input.disabled = disabled; });
        }
      }
    } catch (_) {
      showError();
    } finally {
      checkoutReplay = false;
      checkoutWaiting = false;
    }
  }

  window.MagicGift = { request, sync, get pending() { return pending; } };
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);
  new MutationObserver(() => {
    if (pending || giftFailed) lockExpress(true);
  }).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('pageshow', event => {
    if (event.persisted) sync().catch(() => {});
  });
  document.addEventListener('cart:updated', () => {
    if (rendering || pending || eventScheduled) return;
    eventScheduled = true;
    Promise.resolve().then(() => {
      eventScheduled = false;
      sync().catch(() => {});
    });
  });
  const initialize = () => sync().catch(() => {});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
